<template>
    <div>
        <loader v-if="loading" />
        <chart v-if="!loading" :chartData="chartData"/>
    </div>
</template>

<script>
import Loader from '@/components/Loader.vue';
import Chart from '../StatisticalInformation/Chart'
import moment from 'moment'
import axios from 'axios';
window.Handbook = window.Handbook || {}
const apiUri = window.Handbook.ApiUri || "https://localhandbookapi.aph.gov.au/";
axios.defaults.baseURL = apiUri;

export default {
    components: {
        Chart,
        Loader
    },
    props: ['timeline', 'loading'],
    computed: {
        chartData() {
            var that = this;
            const plotLines = this.$store.getters.GET_PARLIAMENTS_ORDERED
                .map(p => ({
                    value: (new Date(p.DateElection)).valueOf(),
                    dashStyle: 'shortdash',
                    color: '#CCD6EB',
                    label: {
                        text: p.PID,
                        align: 'right',
                        verticalAlign: 'middle',
                        x: 20,
                        y: 3
                    }}));

            const data = this.timeline.ParliamentaryService.map(p => {
                let label = p.Electorate;
                // try to find colour based on dates
                let colour = null;
                const party = this.timeline.PartyService.find(party => p.DateStart >= party.DateStart && p.DateStart <= party.DateEnd)
                if (party) {
                    colour = party.PartyColour;
                } else {
                    colour = this.timeline.PartyService[this.timeline.PartyService.length - 1].PartyColour
                }

                return {
                    x: new Date(p.DateStart).valueOf(),
                    name: '',
                    color: colour,
                    label,
                    description: p.Description,
                    electorate: p.Electorate,
                    memberOrSenator: p.MemberOrSenator
                }
            });
            // also add in party service
            const partyChanges = [];
            this.timeline.PartyService.forEach((p, i) => {
                if (i < this.timeline.PartyService.length - 1) {
                    const nextEntry = this.timeline.PartyService[i + 1];
                    if (p.Party !== nextEntry.Party) {
                        partyChanges.push({
                            x: new Date(nextEntry.DateStart).valueOf(),
                            name: '',
                            label: nextEntry.Party,
                            color: nextEntry.PartyColour,
                            description: `Changed party to ${nextEntry.Party}`                            
                        })
                    }
                }
            });

            // check for Parliamentary gaps
            this.timeline.ParliamentaryService.forEach((p, i) => {
                // check to see if next one exists
                if (i < this.timeline.ParliamentaryService.length - 1) {
                    // get next one, see if there is a gap
                    const nextEntry = this.timeline.ParliamentaryService[i + 1];
                    if (p.DateEnd && new Date(p.DateEnd).addDays(1) < new Date(nextEntry.DateStart)) {
                        let label = '';
                        if (p.Description.includes('Resigned')) {
                            label = 'Resigned';
                        }
                        if (p.Description.includes('Retired')) {
                            label = 'Retired';
                        }
                        else if (p.Description.includes('Defeated')) {
                            label = 'Defeated';
                        }

                        let colour = null;
                        const party = this.timeline.PartyService.find(party => nextEntry.DateStart >= party.DateStart && nextEntry.DateStart <= party.DateEnd)
                        if (party) {
                            colour = party.PartyColour;
                        } else {
                            colour = this.timeline.PartyService[this.timeline.PartyService.length - 1].PartyColour
                        }

                        data.splice(i, 0, {
                            x: new Date(p.DateEnd).valueOf(),
                            name: '',
                            // label should be based on previous entry.
                            label: label,
                            color: colour,
                            description: p.Description,
                            electorate: p.Electorate,
                            memberOrSenator: p.MemberOrSenator
                        });
                    }
                }
            });

            // add another record for the final parliamentary service
            const finalParliamentaryService = this.timeline.ParliamentaryService[this.timeline.ParliamentaryService.length - 1];
            if (finalParliamentaryService && finalParliamentaryService.DateEnd) {
                let label = 'End of Parliamentary service';
                if (finalParliamentaryService.Description.includes('Defeated')) {
                    label = 'Defeated';
                }
                else if (finalParliamentaryService.Description.includes('Died in Office')) {
                    label = 'Died in Office';
                } else if (finalParliamentaryService.Description.includes('Resigned')) {
                    label =  'Resigned';
                } else if (finalParliamentaryService.Description.includes('Retired')) {
                    label =  'Retired';
                }

                let colour = null;
                const party = this.timeline.PartyService.find(party => finalParliamentaryService.DateStart >= party.DateStart && finalParliamentaryService.DateStart <= party.DateEnd)
                if (party) {
                    colour = party.PartyColour;
                } else {
                    colour = this.timeline.PartyService[this.timeline.PartyService.length - 1].PartyColour
                }

                data.push({
                    x: new Date(finalParliamentaryService.DateEnd).valueOf(),
                    name: '',
                    label: label, 
                    // filter description to only show the second part
                    description: finalParliamentaryService.Description,
                    color: colour,
                    electorate: finalParliamentaryService.Electorate,
                    memberOrSenator: finalParliamentaryService.MemberOrSenator
                });
            }

            const cumulativeData = [...data, ...partyChanges];

            // have to merge events that occur on the same day
            const mergedData = [...new Set(cumulativeData.map(d => d.x))].map(date => {
                return {
                    x: date,
                    name: '',
                    label: cumulativeData.filter(d => d.x === date).map(d => d.label).join('<br/>'),
                    description: cumulativeData.filter(d => d.x === date).map(d => d.description).join('<br/>'),
                    color: cumulativeData.find(d => d.x === date).color,
                    electorate: cumulativeData.find(d => d.x === date).electorate,
                    memberOrSenator: cumulativeData.find(d => d.x === date).memberOrSenator
                }
            });
            const dates = cumulativeData.map(d => d.x).sort((a, b) => a - b);

            const firstDate = dates[0];
            const finalDate = dates[dates.length - 1];
            const timeSpan = ((finalDate - firstDate) || 1) / 86400000 / 365.26;

            const height = Math.max(timeSpan * 30, 100);

            return {
                chart: {
                    // height per years
                    spacing: [10, 0, 15, 10],
                    height,
                    type: 'timeline',
                    inverted: true,
                    backgroundColor: '#f5f5f5',
                },
                exporting: {
                    enabled: false
                },
                xAxis: [{
                        type: 'datetime',
                        // minimum one day interval
                        minTickInterval: 24 * 3600 * 1000,
                        plotLines,
                        reversed: false,
                    }, 
                    {
                        title: {
                            text: 'Parliaments'
                        },
                        offset: 15,
                        lineColor: 'transparent',
                        opposite: true
                    }
                ],
                yAxis: {
                    gridLineWidth: 1,
                    title: null,
                    labels: {
                        enabled: false
                    }
                },
                legend: {
                    enabled: false
                },
                title: {
                    text: ''
                },
                tooltip: {
                    outside: true,
                    formatter: function () {
                        return `${new Date(this.x).toLocaleDateString()} - ${this.point.description}`
                    }
                },
                series: [{
                    dataLabels: {
                        allowOverlap: false,
                        events: {
                            click: function () {
                                // construct link
                                // this.point.options
                                // if the event is likely to have an election associated with it, call an API to find out the associated election id, based on the year.
                                const { memberOrSenator, electorate, x } = this.point.options;
                                if (memberOrSenator === 'Member') { 
                                    const date = moment(x).format('YYYY-M-D');
                                    const year = new Date(x).getFullYear();
                                    const url = `Elections/ElectionId?date=${date}`;
                                    axios.get(`api/${url}`)
                                        .then(response => {
                                            // if (memberOrSenator === 'Member') {
                                                that.$router.push({ name: 'DivisionalResult', params: { year, electionId: response.data, division: electorate }});
                                            // }
                                        });
                                    }
                            }
                        },
                        format: '<span style="font-weight: bold;" > ' +
                            '{point.x:%d %b %Y}</span><br/>{point.label}'
                    },
                    marker: {
                        symbol: 'circle'
                    },
                    data: mergedData
                }]
            }
        }
    }
}
</script>